import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowsUpDownIcon,
  ChatBubbleBottomCenterTextIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import FullTable from '../../../components/Table/FullTable';
import {
  deleteInterviewQuestion,
  patchInterviewQuestion,
} from '../../../services/Interviews/interviewQuestionsService';

const statusClass = {
  completed: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
  generating: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
};

export default function QuestionsTable({
  uuid,
  questions,
  isLoading,
  totalCount,
  page,
  setPage,
  pageCount,
  pageSize,
  search,
  setSearch,
  mode,
  setMode,
  selectedKeys,
  setSelectedKeys,
  videoStatus,
  fetchData,
  setQuestions,
}) {
  const isEditMode = mode === 'edit';
  const [originalSequence, setOriginalSequence] = useState(new Map());
  const [saving, setSaving] = useState(false);
  const pageOffset = (page - 1) * pageSize;

  const metrics = useMemo(() => {
    const skills = new Set();
    let followUps = 0;
    questions.forEach(({ question }) => {
      question?.skills?.forEach((skill) => skills.add(skill.uuid));
      followUps += question?.type?.code === 'DYNAMIC_FOLLOWUP' ? 1 : 0;
    });
    const media = Object.values(videoStatus || {}).reduce((result, item) => ({
      completed: result.completed + Number(item.completed || 0),
      total: result.total + Number(item.total || 0),
    }), { completed: 0, total: 0 });
    return { skills: skills.size, followUps, media };
  }, [questions, videoStatus]);

  useEffect(() => {
    if (isEditMode && originalSequence.size === 0) {
      setOriginalSequence(new Map(questions.map((question) => [question.uuid, question.interview_sequence])));
    } else if (!isEditMode && originalSequence.size) {
      setOriginalSequence(new Map());
    }
  }, [isEditMode, originalSequence.size, questions]);

  const finishReordering = async () => {
    const updates = questions.flatMap((question, index) => {
      const sequence = pageOffset + index + 1;
      return originalSequence.get(question.uuid) === sequence
        ? []
        : [patchInterviewQuestion(question.uuid, { interview_sequence: sequence })];
    });
    setSaving(true);
    try {
      await Promise.all(updates);
      if (updates.length) toast.success(`Saved the order of ${updates.length} question${updates.length === 1 ? '' : 's'}.`);
      setMode('view');
      setSelectedKeys(new Set());
      fetchData();
    } catch (error) {
      toast.error('Question order could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedKeys.size || !window.confirm(`Remove ${selectedKeys.size} selected question${selectedKeys.size === 1 ? '' : 's'} from this interview?`)) return;
    setSaving(true);
    const results = await Promise.allSettled(Array.from(selectedKeys).map(deleteInterviewQuestion));
    const failed = results.filter((result) => result.status === 'rejected').length;
    if (failed) toast.error(`${failed} question${failed === 1 ? '' : 's'} could not be removed.`);
    else toast.success('Selected questions were removed from the interview.');
    setSelectedKeys(new Set());
    setSaving(false);
    fetchData();
  };

  const columns = [
    {
      key: 'interview_sequence', label: 'Order', className: 'w-20 text-center',
      render: (item) => <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-gray-100 px-2 font-semibold dark:bg-gray-700">{item.interview_sequence}</span>,
    },
    {
      key: 'question', label: 'Question', className: 'min-w-[18rem]',
      render: (item) => <div><p className="font-semibold text-gray-950 dark:text-white">{item.question?.name || 'Untitled question'}</p><p className="mt-1 max-w-2xl text-sm text-gray-500">{item.question?.body}</p><div className="mt-2 flex flex-wrap gap-1.5">{item.question?.skills?.length ? item.question.skills.map((skill) => <span key={skill.uuid} className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-200">{skill.name}</span>) : <span className="text-xs text-gray-400">No managed skills</span>}</div></div>,
    },
    {
      key: 'followups', label: 'Follow-ups', className: 'w-28 text-center',
      render: (item) => <span className="inline-flex items-center gap-1 text-sm"><ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-gray-400" />{item.question?.type?.code === 'DYNAMIC_FOLLOWUP' ? 'Adaptive' : '—'}</span>,
    },
    {
      key: 'media', label: 'AI media', className: 'min-w-[12rem]',
      render: (item) => {
        const agents = item.agents || [];
        return agents.length ? <div className="flex flex-wrap gap-1">{agents.map(([name, status]) => <span key={name} title={name} className={`rounded-full px-2 py-1 text-xs font-medium ${statusClass[status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200'}`}>{name}: {status || 'not generated'}</span>)}</div> : <span className="text-xs text-gray-400">Not generated</span>;
      },
    },
  ];

  return (
    <section aria-labelledby="questions-title" className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ['Questions', totalCount, 'Included in this interview'],
          ['Managed skills', metrics.skills, 'Visible on this page'],
          ['AI follow-ups', metrics.followUps, 'Questions with adaptive probing on this page'],
          ['Media ready', `${metrics.media.completed}/${metrics.media.total}`, 'Across AI presenters'],
        ].map(([label, value, detail]) => <article key={label} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"><strong className="text-2xl">{value}</strong><h3 className="mt-1 font-semibold">{label}</h3><p className="text-xs text-gray-500">{detail}</p></article>)}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col justify-between gap-4 border-b border-gray-200 p-5 dark:border-gray-700 lg:flex-row lg:items-end">
          <div><h2 id="questions-title" className="text-xl font-bold">Interview questions</h2><p className="mt-1 text-sm text-gray-500">Search the current set, add questions from the bank, or reorder them into the student journey.</p></div>
          <Link to={`/organization/interviews/${uuid}/questions/add`} className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"><PlusIcon className="h-5 w-5" />Add from question bank</Link>
        </div>

        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 dark:border-gray-700 md:flex-row md:items-center">
          <label className="flex-1 text-sm font-medium">Search questions<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by title or question text" className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900" /></label>
          <div className="flex flex-wrap items-center gap-2 md:self-end">
            <button type="button" disabled={saving} onClick={isEditMode ? finishReordering : () => { setSelectedKeys(new Set()); setMode('edit'); }} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:hover:bg-gray-700"><ArrowsUpDownIcon className="h-4 w-4" />{isEditMode ? (saving ? 'Saving…' : 'Save order') : 'Reorder and select'}</button>
            {isEditMode && selectedKeys.size > 0 && <button type="button" disabled={saving} onClick={handleDelete} className="inline-flex items-center gap-2 rounded-xl border border-red-300 px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:text-red-300"><TrashIcon className="h-4 w-4" />Remove selected ({selectedKeys.size})</button>}
          </div>
        </div>

        {isEditMode && <p className="mx-4 mt-4 rounded-xl bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-950 dark:text-blue-200">Drag rows using the handle to change their order. Select rows only when you want to remove them.</p>}
        <div className="overflow-x-auto p-4"><FullTable data={questions} showOrderNumber={false} columns={columns} loading={isLoading} enableSearch={false} enableCheckbox={isEditMode} enableDnd={isEditMode} emptyMessage={search ? 'No questions match your search.' : 'No questions have been added yet. Use “Add from question bank” to get started.'} onSelectionChange={(ids) => setSelectedKeys(new Set(ids))} onReorder={(items) => setQuestions(items.map((item, index) => ({ ...item, interview_sequence: pageOffset + index + 1 })))} /></div>

        <div className="flex flex-col justify-between gap-3 border-t border-gray-200 p-4 text-sm dark:border-gray-700 sm:flex-row sm:items-center">
          <span className="text-gray-500">Showing {questions.length} of {totalCount} question{totalCount === 1 ? '' : 's'}</span>
          <nav aria-label="Question pagination" className="flex items-center gap-2"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Previous</button><span>Page {page} of {Math.max(pageCount, 1)}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Next</button></nav>
        </div>
      </div>
    </section>
  );
}
